import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import ServiceState from './ServiceState';
import ServiceActions from './ServiceActions';
import RunningNow from './RunningNow';
import UsageMeters, { UsageBreakdown } from './UsageMeters';
import { AI_TOOLS, connectionOf } from './ConnectToolModal';
import ToolIcon from './ToolIcon';
import { PlanChangeButton, PlanPill } from './PlanRequest';
import { SandboxTypePill } from './SandboxTypePicker';
import SandboxWaking from './SandboxWaking';
import { ListRowsSkeleton, SkeletonBar } from '../Skeletons';
import { formatBytes, formatCores, formatUptime, isRunning } from '../../services/SandboxService';
import { formatRelativeTime } from '../../utils/relativeTime';
import './sandbox.css';
import '../../pages/SandboxHome.css';

/**
 * The sandbox's sections, shared by the two faces of Home (docs/SANDBOX_FIRST.md): the full sandbox overview when Chat
 * is off (pages/SandboxHome.jsx) and, under the prompt, the compact SandboxSummary when Chat is on.
 */

/** The AI tools that reached the sandbox: what it saw call (aiTools), else the tools that made a recent project. */
export function connectedToolsOf(sandbox, recent = []) {
  const madeWith = new Set(recent.map((project) => project.createdWith).filter(Boolean));
  return AI_TOOLS
    .map((item) => ({ item, seen: connectionOf(item, sandbox) || (madeWith.has(item.name) ? { lastSeenAt: null } : null) }))
    .filter(({ seen }) => seen);
}

/** Work from your AI tool: ONE call to action, and one line naming the tools already connected. */
export function AiToolsSection({ sandbox, recent = [], onConnect, onOpenTool, loading = false }) {
  const connected = connectedToolsOf(sandbox, recent);
  // Until the sandbox answers, which tools are connected is not known: a line-sized placeholder, not "none yet".
  const waiting = loading && !sandbox;
  return (
    <section aria-labelledby="sbx-tools" className="sbx-card sbx-home-section">
      <div className="sbx-home-section-head">
        <h2 id="sbx-tools">Work from your AI tool</h2>
        <Button onClick={onConnect} type={connected.length || waiting ? 'default' : 'primary'}>Connect your AI tool</Button>
      </div>
      {waiting && <span aria-busy="true" aria-label="Loading your AI tools" className="sbx-home-split"><SkeletonBar height={12} width="45%" /></span>}
      {!waiting && connected.length === 0 && (
        <span className="sbx-home-split">Claude, GitHub Copilot, Cursor, Antigravity, Mistral, Kiro and other MCP clients get your projects, a shell and live previews with one connection.</span>
      )}
      {!waiting && connected.length > 0 && (
        <p className="sbx-home-split sbx-tools-line">
          Connected:{' '}
          {connected.map(({ item, seen }, index) => (
            <React.Fragment key={item.key}>
              {index > 0 ? ', ' : ''}
              <button className="sbx-link sbx-tool-link" onClick={() => onOpenTool(item)} type="button"><ToolIcon icon={item.icon} label={item.name} size={14} />{item.name}</button>
              {seen.lastSeenAt ? ` · last used ${formatRelativeTime(seen.lastSeenAt)}` : ''}
            </React.Fragment>
          ))}
        </p>
      )}
    </section>
  );
}

AiToolsSection.propTypes = {
  sandbox: PropTypes.object,
  recent: PropTypes.array,
  onConnect: PropTypes.func.isRequired,
  onOpenTool: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

/** Running now: every service that runs (or just crashed / failed to build), with its own CPU and memory. */
export function RunningNowSection({ sandbox, onOpenProject, onOpenLogs, onChanged, onStopAll, stoppingAll = false, loading = false }) {
  const services = sandbox?.services || [];
  const waiting = loading && !sandbox;
  const live = services.filter((service) => isRunning(service) || service.crashed || service.buildStatus === 'failed');
  return (
    <section aria-labelledby="sbx-running" className="sbx-card sbx-home-section sbx-home-table-card">
      <div className="sbx-home-section-head sbx-pad">
        <h2 id="sbx-running">Running now</h2>
        {live.some(isRunning) && <Button loading={stoppingAll} onClick={() => onStopAll(live.filter(isRunning))}>Stop all</Button>}
      </div>
      {waiting && <div className="sbx-running-skeleton"><ListRowsSkeleton label="Loading what runs now" rows={2} /></div>}
      {sandbox?.servicesUnavailable && <p className="sbx-home-empty">The service list needs a newer preview runner.</p>}
      {!waiting && !sandbox?.servicesUnavailable && live.length === 0 && (
        <p className="sbx-home-empty">Nothing is running. Open a project and start a service, or ask your AI tool for a preview.</p>
      )}
      {live.length > 0 && (
        <div className="sbx-table-wrap">
          <table className="sbx-table">
            <thead><tr><th>Project</th><th>Service</th><th>State</th><th title="Cores this service uses now (its build and dev server)">CPU</th><th>Memory</th><th>Up</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {live.map((service) => (
                <tr key={service.sessionId}>
                  <td><button className="sbx-link" onClick={() => onOpenProject(service.projectId)} type="button">{service.projectName || service.projectId}</button></td>
                  <td className="sbx-mono">{service.serviceName}</td>
                  <td><ServiceState service={service} /></td>
                  <td>{service.resources ? formatCores(service.resources.cpuPercent) : '—'}</td>
                  <td>{formatBytes(service.resources?.memoryBytes)}</td>
                  <td>{formatUptime(service.startedAt || service.createdAt)}</td>
                  <td className="sbx-actions-cell">
                    <ServiceActions
                      onChanged={onChanged}
                      onOpenLogs={(name) => onOpenLogs(service.projectId, name)}
                      projectId={service.projectId}
                      service={service}
                      serviceName={service.serviceName}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

RunningNowSection.propTypes = {
  sandbox: PropTypes.object,
  onOpenProject: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
  onStopAll: PropTypes.func.isRequired,
  stoppingAll: PropTypes.bool,
  loading: PropTypes.bool,
};

/** The sandbox's state as a pill: running, asleep, waking, not reachable, signed out. */
export function SandboxStatePill({ state, loading = false, waking = false }) {
  if (loading) {
    return waking
      ? <span className="sbx-pill sbx-pill-building" role="status"><span aria-hidden="true" className="sbx-dot" />Waking your sandbox…</span>
      : null;
  }
  return (
    <span className={`sbx-pill ${state === 'running' ? 'sbx-pill-running' : ''}`}>
      {state === 'running' ? <span aria-hidden="true" className="sbx-dot" /> : null}
      {state === 'running' ? 'Running' : state === 'unreachable' ? 'Not reachable' : state === 'signed-out' ? 'Signed out' : 'Asleep'}
    </span>
  );
}

SandboxStatePill.propTypes = { state: PropTypes.string, loading: PropTypes.bool, waking: PropTypes.bool };

const COMPACT_METERS = ['cpu', 'memory', 'disk', 'services'];

/** Four meter-shaped placeholders, so the strip keeps its height while the sandbox answers (or wakes). */
export const MetersSkeleton = () => (
  <div aria-busy="true" aria-label="Loading your sandbox's usage" className="sbx-meters sbx-meters-compact">
    {COMPACT_METERS.map((key) => (
      <div className="sbx-meter" data-testid="meter-skeleton" key={key}>
        <SkeletonBar height={12} width="45%" />
        <SkeletonBar height={20} width="60%" />
        <SkeletonBar height={6} radius={9} />
        <SkeletonBar height={11} width="70%" />
      </div>
    ))}
  </div>
);

/**
 * Home with Chat on: the sandbox under the prompt, compact — its state and usage in one strip, what runs now, and how
 * to work on it from an AI tool.
 */
export function SandboxSummary({
  sandbox, state, loading = false, waking = false, error = null, warnings = [], actions = null, recent = [],
  onConnect, onOpenTool, onOpenProject, onOpenLogs, onChanged, onStopAll, stoppingAll = false, onRetry, tenantId = null, sleepAction = null,
}) {
  return (
    <div aria-label="Your sandbox" className="sbx-summary" role="region">
      <section className="sbx-card sbx-home-section sbx-summary-strip">
        <div className="sbx-home-section-head">
          <div className="sbx-home-title-row">
            <h2 id="sbx-summary-title">Your sandbox</h2>
            <SandboxStatePill loading={loading} state={state} waking={waking} />
            <PlanPill sandbox={sandbox} />
            <SandboxTypePill sandbox={sandbox} />
          </div>
          {actions}
        </div>
        {error && (
          <span className="sbx-home-split" role="alert">
            {error.status === 401 ? 'Your Flow session has expired: sign in again to see your sandbox.' : `Your sandbox could not be reached (${error.message}).`}
            {error.status !== 401 && onRetry ? <> <button className="sbx-link" onClick={onRetry} type="button">Retry</button></> : null}
          </span>
        )}
        {/* Why the sandbox (or Flow) makes the person wait, nothing while it runs (SandboxWaking). */}
        <SandboxWaking compact watch={loading} />
        {warnings.length > 0 && (
          <span className="sbx-home-split sbx-summary-warn" role="status">
            {warnings.map((warning) => warning.message).join(' ')}
            {' '}<PlanChangeButton />
          </span>
        )}
        {loading && !sandbox ? <MetersSkeleton /> : <UsageMeters only={COMPACT_METERS} sandbox={sandbox} />}
        <UsageBreakdown sandbox={sandbox} />
      </section>
      {/* The same grouped card as the full Home (RunningNow): projects collapsed, service name → Code, Open → Preview. */}
      <RunningNow
        loading={loading}
        onChanged={onChanged}
        onOpenLogs={onOpenLogs}
        onOpenProject={onOpenProject}
        onStopAll={onStopAll}
        sandbox={sandbox}
        sleepAction={sleepAction}
        stoppingAll={stoppingAll}
        tenantId={tenantId}
      />
      <AiToolsSection loading={loading} onConnect={onConnect} onOpenTool={onOpenTool} recent={recent} sandbox={sandbox} />
    </div>
  );
}

SandboxSummary.propTypes = {
  sleepAction: PropTypes.node,
  sandbox: PropTypes.object,
  state: PropTypes.string,
  loading: PropTypes.bool,
  waking: PropTypes.bool,
  error: PropTypes.object,
  warnings: PropTypes.array,
  actions: PropTypes.node,
  recent: PropTypes.array,
  onConnect: PropTypes.func.isRequired,
  onOpenTool: PropTypes.func.isRequired,
  onOpenProject: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
  onStopAll: PropTypes.func.isRequired,
  stoppingAll: PropTypes.bool,
  onRetry: PropTypes.func,
  tenantId: PropTypes.string,
};
